import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { fetchCategories, upsertCategory } from "../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, AlertCircle, Loader2, ChevronDown, X, Tag } from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

const ECategory: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const categories = useSelector((state: RootState) => state.blog.categories);

  /* ── form state (all original) ── */
  const [name,        setName]        = useState("");
  const [slug,        setSlug]        = useState("");
  const [description, setDescription] = useState("");
  const [parent,      setParent]      = useState<string | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [slugEdited,  setSlugEdited]  = useState(false);
  const [toast,       setToast]       = useState<{ type: "success"|"danger"; text: string } | null>(null);

  useEffect(() => { dispatch(fetchCategories()); }, [dispatch]);

  /* ── Slug auto-gen ── */
  const generateSlug = (text: string) =>
    text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

  useEffect(() => {
    if (!slugEdited) setSlug(generateSlug(name));
  }, [name, slugEdited]);

  const flash = (type: "success"|"danger", text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { flash("danger", "Please enter a category name."); return; }
    setLoading(true);
    const finalSlug = slug.trim() !== "" ? slug : generateSlug(name);
    try {
      await dispatch(upsertCategory({
        title: name, slug: finalSlug, description, parent,
      })).unwrap();
      flash("success", `Category "${name}" saved successfully!`);
      setName(""); setSlug(""); setDescription(""); setParent(null); setSlugEdited(false);
      dispatch(fetchCategories());
    } catch (err) {
      console.error("Failed to save category", err);
      flash("danger", "Failed to save category. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ background: C.paper, fontFamily: C.sans, color: C.ink }}>
      <style>{FONT_CSS + EC_CSS}</style>

      {/* ── TITLE STRIP ── */}
      <div style={S.titleStrip}>
        <div style={{ height: 2, background: C.ink, marginBottom: 16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.eye}>CONTENT MANAGEMENT</p>
            <h1 style={S.title}>Categories</h1>
          </div>
          <span style={S.countPill}>
            {categories.length} categor{categories.length !== 1 ? "ies" : "y"}
          </span>
        </div>
        <div style={{ height: 1, background: C.border }}/>
      </div>

      <div style={S.body}>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast && (
            <motion.div
              key="toast"
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"12px 16px", marginBottom:24,
                background: toast.type==="success" ? C.successBg : C.dangerBg,
                border: `1px solid ${toast.type==="success" ? C.successBorder : C.dangerBorder}`,
              }}
            >
              {toast.type === "success"
                ? <CheckCircle size={14} color={C.success}/>
                : <AlertCircle size={14} color={C.danger}/>
              }
              <span style={{ fontSize:12, fontWeight:500, flex:1,
                color: toast.type==="success" ? C.success : C.danger }}>
                {toast.text}
              </span>
              <button onClick={() => setToast(null)}
                style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}>
                <X size={12}/>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="ec-layout">

          {/* ════════════════════════════
              LEFT — ADD CATEGORY FORM
          ════════════════════════════ */}
          <form onSubmit={handleSubmit} style={S.form}>
            <div style={S.sectionHead}>
              <div style={{ width:24, height:2, background:C.ink, flexShrink:0 }}/>
              <h2 style={S.sectionTitle}>Add New Category</h2>
            </div>

            {/* Name */}
            <div style={S.field}>
              <label style={S.label}>
                NAME <span style={{ color:C.danger }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="The name is how it appears on your site."
                style={S.titleInput}
                className="ec-input ec-name"
              />
            </div>

            {/* Slug */}
            <div style={S.field}>
              <label style={S.label}>SLUG</label>
              <div style={S.slugWrap}>
                <span style={S.slugPrefix}>site.com/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={e => { setSlug(e.target.value.toLowerCase().replace(/[^\w-]/g,"-")); setSlugEdited(true); }}
                  placeholder="auto-generated"
                  style={S.slugInput}
                  className="ec-input"
                />
                {slugEdited && (
                  <button type="button"
                    onClick={() => { setSlug(generateSlug(name)); setSlugEdited(false); }}
                    style={S.slugReset}>
                    Reset
                  </button>
                )}
              </div>
              <p style={S.hint}>
                URL-friendly version — auto-generated from the name.
              </p>
            </div>

            {/* Parent Category */}
            <div style={S.field}>
              <label style={S.label}>PARENT CATEGORY</label>
              <div style={{ position:"relative" }}>
                <select
                  value={parent || ""}
                  onChange={e => setParent(e.target.value || null)}
                  style={S.select}
                  className="ec-input"
                >
                  <option value="">— None —</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.title}</option>
                  ))}
                </select>
                <ChevronDown size={13} color={C.inkMid} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}/>
              </div>
              <p style={S.hint}>
                Categories can have a hierarchy — e.g. Jazz → Bebop, Big Band.
              </p>
            </div>

            {/* Description */}
            <div style={S.field}>
              <label style={S.label}>DESCRIPTION</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional — may be shown by some themes."
                rows={3}
                style={S.textarea}
                className="ec-input"
              />
            </div>

            {/* Submit */}
            <motion.button
              whileTap={{ scale: 0.97 } as any}
              type="submit"
              disabled={loading}
              style={{
                ...S.submitBtn,
                opacity: loading ? 0.65 : 1,
                cursor:  loading ? "not-allowed" : "pointer",
              }}
              className="ec-submit-btn"
            >
              {loading
                ? <><Loader2 size={14} className="ec-spin" style={{ marginRight:8 }}/> SAVING…</>
                : "ADD CATEGORY"
              }
            </motion.button>
          </form>

          {/* ════════════════════════════
              RIGHT — EXISTING CATEGORIES
          ════════════════════════════ */}
          <div style={S.sidebar}>
            <div style={S.sectionHead}>
              <div style={{ width:24, height:2, background:C.ink, flexShrink:0 }}/>
              <h2 style={S.sectionTitle}>All Categories</h2>
            </div>

            {categories.length === 0 ? (
              <div style={S.empty}>
                <Tag size={28} color={C.border} strokeWidth={1}/>
                <p style={{ fontSize:13, color:C.inkLight, marginTop:10 }}>No categories yet</p>
                <p style={{ fontSize:11, color:C.inkLight, marginTop:4, textAlign:"center" as const }}>
                  Create your first category using the form
                </p>
              </div>
            ) : (
              <div>
                {/* Header row */}
                <div style={S.tableHead}>
                  <span style={{ flex:1 }}>NAME</span>
                  <span style={{ width:120 }}>SLUG</span>
                  <span style={{ width:80, textAlign:"right" as const }}>CHILDREN</span>
                </div>

                {categories.map((cat, idx) => {
                  const childCount = categories.filter(c => c.parent === cat.slug).length;
                  const isLast     = idx === categories.length - 1;
                  return (
                    <motion.div
                      key={cat.id}
                      initial={{ opacity:0, y:6 }}
                      animate={{ opacity:1, y:0 }}
                      transition={{ delay: idx * 0.04 }}
                      style={{
                        ...S.catRow,
                        borderBottom: isLast ? `1px solid ${C.border}` : "none",
                      }}
                      className="ec-row"
                    >
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={S.catName}>{cat.title}</div>
                        {cat.description && (
                          <div style={S.catDesc}>
                            {cat.description.slice(0,60)}{cat.description.length>60?"…":""}
                          </div>
                        )}
                        {cat.parent && (
                          <div style={S.catParentTag}>
                            ↳ {cat.parent}
                          </div>
                        )}
                      </div>
                      <div style={{ width:120, minWidth:0 }}>
                        <span style={S.catSlug}>/{cat.slug}</span>
                      </div>
                      <div style={{ width:80, textAlign:"right" as const }}>
                        {childCount > 0 ? (
                          <span style={S.catBadge}>{childCount}</span>
                        ) : (
                          <span style={{ fontSize:10, color:C.border }}>—</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ECategory;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  titleStrip: { padding:"20px 20px 0" },
  eye:   { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  title: { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  countPill: { fontSize:10, letterSpacing:"0.14em", color:C.inkMid, fontFamily:C.sans, paddingBottom:4 },

  body:        { maxWidth:1160, margin:"0 auto", padding:"28px 20px 60px" },
  form:        { display:"flex", flexDirection:"column", gap:18 },
  sectionHead: { display:"flex", alignItems:"center", gap:14, marginBottom:4 },
  sectionTitle:{ fontFamily:C.serif, fontSize:22, fontWeight:600, color:C.ink, margin:0 },

  field:   { display:"flex", flexDirection:"column", gap:7 },
  label:   { fontSize:9, letterSpacing:"0.22em", color:C.inkMid, fontFamily:C.sans, fontWeight:600 },
  hint:    { fontSize:10, color:C.inkLight, marginTop:5, lineHeight:1.6, fontFamily:C.sans },

  titleInput: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"13px 16px", fontSize:16, fontWeight:500, color:C.ink,
    fontFamily:C.serif, outline:"none", boxSizing:"border-box" as const,
    letterSpacing:"0.01em", transition:"border-color 0.2s",
  },
  slugWrap:   { display:"flex", alignItems:"center", border:`1px solid ${C.border}`, background:C.paper },
  slugPrefix: { fontSize:11, color:C.inkLight, padding:"0 12px", flexShrink:0, borderRight:`1px solid ${C.border}`, lineHeight:"42px" },
  slugInput:  { flex:1, background:"transparent", border:"none", outline:"none", padding:"11px 12px", fontSize:12, color:C.ink, fontFamily:"'Courier New',monospace", letterSpacing:"0.02em" },
  slugReset:  { fontSize:9, color:C.inkMid, letterSpacing:"0.1em", background:"none", border:"none", cursor:"pointer", padding:"0 12px", fontFamily:C.sans, flexShrink:0 },

  select: {
    width:"100%", appearance:"none" as const, background:C.surface,
    border:`1px solid ${C.border}`, padding:"11px 36px 11px 12px",
    fontSize:12, color:C.ink, fontFamily:C.sans, outline:"none", cursor:"pointer",
  },
  textarea: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"11px 14px", fontSize:13, color:C.ink,
    fontFamily:C.sans, fontWeight:300, outline:"none",
    resize:"vertical", boxSizing:"border-box" as const,
    transition:"border-color 0.2s", lineHeight:1.75,
  },

  submitBtn: {
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    background:C.ink, color:"#fff", border:"none",
    padding:"13px 28px", fontSize:10, fontWeight:700,
    letterSpacing:"0.22em", fontFamily:C.sans,
    alignSelf:"flex-start" as const, transition:"opacity 0.2s",
  },

  /* Sidebar */
  sidebar: { position:"sticky" as const, top:20 },
  empty:   { display:"flex", flexDirection:"column", alignItems:"center", padding:"48px 20px", textAlign:"center" as const },

  /* Table */
  tableHead: {
    display:"flex", alignItems:"center", gap:16,
    padding:"8px 0 8px", borderBottom:`2px solid ${C.ink}`,
    fontSize:8, letterSpacing:"0.22em", color:C.inkMid,
    fontFamily:C.sans, fontWeight:600,
  },
  catRow: {
    display:"flex", alignItems:"center", gap:16,
    padding:"14px 0", borderTop:`1px solid ${C.border}`,
    cursor:"default",
  },
  catName: {
    fontSize:13, fontWeight:500, color:C.ink,
    fontFamily:C.serif, letterSpacing:"0.01em",
    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const,
  },
  catDesc: {
    fontSize:10, color:C.inkLight, marginTop:2,
    fontFamily:C.sans, fontWeight:300, lineHeight:1.5,
  },
  catParentTag: {
    fontSize:9, color:C.inkMid, marginTop:3,
    fontFamily:C.sans, letterSpacing:"0.06em",
  },
  catSlug: {
    fontSize:10, color:C.inkLight,
    fontFamily:"'Courier New',monospace",
    overflow:"hidden", textOverflow:"ellipsis",
    whiteSpace:"nowrap" as const, display:"block",
  },
  catBadge: {
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    width:20, height:20, borderRadius:"50%",
    background:C.ink, color:"#fff",
    fontSize:9, fontWeight:700, fontFamily:C.sans,
  },
};

/* ─────────────────────────────────────────────────────────
   CSS
───────────────────────────────────────────────────────── */
const EC_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  .ec-input:focus  { border-color: #0a0a0a !important; }
  .ec-name:focus   { border-color: #0a0a0a !important; }
  input::placeholder  { color: #a3a3a3; font-size: 12px; }
  textarea::placeholder { color: #a3a3a3; font-size: 12px; line-height: 1.7; }

  .ec-row:hover { background: #f7f7f7; }

  @keyframes ec-spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  .ec-spin { animation: ec-spin 1s linear infinite; }

  /* ── Desktop: two-column ── */
  .ec-layout {
    display: grid;
    grid-template-columns: 420px 1fr;
    gap: 48px;
    align-items: start;
  }

  /* ── Mobile: single column ── */
  @media (max-width: 720px) {
    .ec-layout {
      grid-template-columns: 1fr !important;
      gap: 36px !important;
    }
    .ec-layout > div:last-child {
      position: relative !important;
      top: 0 !important;
    }
    .ec-submit-btn { width: 100% !important; align-self: stretch !important; }
  }
`;